import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTint, FaUserAlt, FaEuroSign, FaCheck, FaTimes, FaMinus, FaExclamationTriangle } from 'react-icons/fa';
import WaterUsageChart from '../dashboardComponents/WaterUsageChart.js';
import MapConsumption from '../dashboardComponents/MapConsumption.js';
import WalterLoader from '../shared/WalterLoader';
import '../../styles/ClientDashboard.css';
import '../../styles/DashboardComponents.css';

const ClientDashboard = () => {
  const { client_id } = useParams();
  const navigate = useNavigate();

  const [dataClient, setDataClient] = useState(true);
  const [buildings, setBuildings] = useState([]);
  
  const [loadingClient, setLoadingClient] = useState(false);
  const [loadingBuildings, setLoadingBuildings] = useState(false);

  const [error, setError] = useState(null);

  const formatValue = (value) => {
    if (value !== undefined && value !== null) {
      return value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    }
    return '';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getUTCDate() || '';
    const month = date.toLocaleString('fr-FR', { month: 'long' }) || '';
    const year = date.getUTCFullYear() || '';
    return `${day} ${month} ${year}`;
  };

  const getColorForConsumptionRatio = (ratio) => {
    if (ratio < 100/1000) return 'green';
    if (ratio < 200/1000) return 'orange';
    return 'red';
  };

  // Extrait un message d'erreur exploitable d'une réponse HTTP en échec : le
  // backend renvoie parfois du JSON ({"error": "..."}), parfois une page
  // d'erreur HTML brute (ex: exception non interceptée) qui ne se parse pas.
  const parseErrorResponse = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      if (data && data.error) return data.error;
    } catch (e) {
      // Réponse non-JSON (page d'erreur HTML par ex.) : on garde le fallback
    }
    return `${fallbackMessage} (HTTP ${response.status})`;
  };

  // Données clients
  useEffect(() => {
    setLoadingClient(true);
    if (client_id) {
      fetch(`${process.env.REACT_APP_API_URL}/get_client_data_by_id/${client_id}`)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(await parseErrorResponse(response, "Erreur lors de la récupération du client"));
          }
          return response.json();
        })
        .then((data) => {
          setDataClient(data);
        })
        .catch((err) => setError(err.message))
        .finally(() => setLoadingClient(false));
    }
  }, [client_id]);

  // Données bâtiments
  useEffect(() => {
    const fetchBuildings = async () => {
      setLoadingBuildings(true);
      if (client_id) {
        setError(null);
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/get_buildings_from_client_id/${client_id}`);
          if (!response.ok) {
            throw new Error(await parseErrorResponse(response, "Erreur lors de la récupération des bâtiments"));
          }
          const data = await response.json();

          const consumptionResponse = await fetch(`${process.env.REACT_APP_API_URL}/get_buildings_total_consumption`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
          });
          if (!consumptionResponse.ok) {
            throw new Error(await parseErrorResponse(consumptionResponse, "Erreur lors du calcul de la consommation des bâtiments"));
          }
          const buildingsWithConsumption = await consumptionResponse.json();
          setBuildings(buildingsWithConsumption);
        } catch (err) {
          setError(err.message);
        }
        setLoadingBuildings(false);
      }
    };
    fetchBuildings();
  }, [client_id]);

  const handleBuildingClick = (buildingId) => {
    navigate(`/client/${client_id}/${buildingId}`);
  };

  const totalConsumption = buildings.reduce((sum, building) => sum + (building.total_consumption || 0), 0);

  const mapChartData = buildings.reduce((acc, building) => {
    acc[building.name_building] = building.total_consumption || 0;
    return acc;
  }, {});

  if (loadingClient || loadingBuildings) return (
    <div className="centered-spinner-dashboard">
      <WalterLoader />
    </div>
  );
  if (error) return (
    <div className="dashboard-error-state">
      <FaExclamationTriangle className="dashboard-error-state-icon" />
      <h2 className="dashboard-error-state-title">Impossible d'afficher le tableau de bord</h2>
      <p className="dashboard-error-state-message">{error}</p>
    </div>
  );

    return (

      <div className="building-dashboard-container client-dashboard-layout">

        <div className="client-dashboard-synthesis">
          <div className="client-dashboard-synthesis-left">
            <h1 className="client-dashboard-client-name">{dataClient?.name_client || "Inconnu"}</h1>
            <p className="client-dashboard-synthesis-label">Synthèse</p>
            <div className="client-dashboard-two-numbers">
              <div className="client-dashboard-number">
                <strong>{formatValue(totalConsumption)} m³/an</strong>
                <span>Consommation totale</span>
              </div>
              <div className="client-dashboard-number">
                <strong>{buildings.length}</strong>
                <span>{buildings.length > 1 ? "Bâtiments audités" : "Bâtiment audité"}</span>
              </div>
            </div>
          </div>
          {dataClient?.logo_url && (
            <img className="client-dashboard-logo" src={dataClient.logo_url} alt="Client Logo" />
          )}
        </div>

        <div className="client-action-plan-graphs">
          <div className="client-action-plan-dashboard-item">
            <h2>Répartition des consommations par bâtiment</h2>
            <WaterUsageChart data={mapChartData} unit="m³" />
          </div>
          <div className="client-action-plan-dashboard-item">
            <h2>Les bâtiments</h2>
            <MapConsumption buildings={buildings} />
          </div>
        </div>

        <div className="client-dashboard-building-list-container">
          <div className="client-dashboard-section-header">
            <span>Liste des bâtiments</span>
          </div>
          <table className="tab-1-table">
            <thead>
              <tr>
                <th className="tab-1-header-building">Bâtiment</th>
                <th className="tab-1-header">Consommation</th>
                <th className="tab-1-header">Niveau de consommation</th>
                <th className="tab-1-header">Surface</th>
                <th className="tab-1-header">Date d'audit</th>
              </tr>
            </thead>
            <tbody>
              {buildings.map((building) => {
                const hasRatio = !!building.surface && building.total_consumption != null;
                const ratio = hasRatio ? building.total_consumption / building.surface : null;
                const color = hasRatio ? getColorForConsumptionRatio(ratio) : null;
                return (
                  <tr
                    className="tab-1-row"
                    key={building.id}
                    onClick={() => handleBuildingClick(building._id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="tab-1-cell-building">{building.name_building}</td>
                    <td className="tab-1-cell">{formatValue(building.total_consumption)} m³</td>
                    <td className="tab-1-cell">
                      {color ? (
                        <span className={`risk-level-badge risk-level-${color}`}>
                          <span className="risk-level-dot"></span>
                          {color === 'green' ? 'Faible' : color === 'orange' ? 'Modéré' : 'Élevé'}
                        </span>
                      ) : (
                        <span className="risk-level-badge risk-level-unknown">
                          <span className="risk-level-dot"></span>
                          Inconnu
                        </span>
                      )}
                    </td>
                    <td className="tab-1-cell">{formatValue(building.surface)} m²</td>
                    <td className="tab-1-cell">{formatDate(building.audit_date)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* <div className="client-dashboard-building-list-container-v2">
          {buildings.map((building) => (
            <div
              key={building.id}
              className="client-dashboard-building-rectangle"
              onClick={() => handleBuildingClick(building._id)}
            >
              <div className='client-dashboard-icon-and-label'>
                <img src={`/icons/${building.type_building}.png`} alt={building.building_type} className="client-dashboard-building-icon" />
                <div>
                  <div className="client-dashboard-building-name">{building.name_building}</div>
                  <div className="client-dashboard-building-details">{building.adress}</div>
                  <div className="client-dashboard-building-details">{building.postal_code} {building.city}</div>
                  <div className="client-dashboard-building-details">{formatValue(building.surface)} m²</div>
                </div>
              </div>
              <div className="client-dashboard-water-consumption">
                <FaTint className="client-dashboard-icon-tint" />
                <div>{formatValue(Math.round(building.total_consumption))} m³/an</div>
              </div>
            </div>
          ))}
        </div> */}
      </div>
    );
    
};

export default ClientDashboard;
